/** The frame of the person's own pages - My profile and My settings (ADR-0212): the table page's
 * header card, and sections beneath it that are cards with a heading, an optional info control and
 * their controls.
 *
 * **A settings page is a form, not a data screen**, so it is held to the form budget (40 words and
 * two sentences, `textBudget.ts`): a section carries a heading and its controls, and everything that
 * explains a control - what it changes, what it does not, why it is not offered - is behind the info
 * control (`infoText.ts`, section `account`). It holds no table: the grid is for data places.
 *
 * `NotYet` is the one way this product says a thing is not available: a label and the words "Not
 * available yet", never a disabled control. A disabled control implies it will be enabled; this says
 * what is true, and the reason is behind the info control where there is one.
 */

import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { InfoPopover, StatusChip, TablePage } from "../components/ui";
import type { InfoKey } from "../shell/infoText";
import styles from "./SettingsPage.module.css";

export function SettingsPage({
  icon,
  title,
  label,
  children,
}: {
  icon: Icon;
  title: string;
  label?: string;
  children: ReactNode;
}) {
  return (
    <TablePage icon={icon} title={title} label={label} state={<div className={styles.stack}>{children}</div>} />
  );
}

export function SettingsSection({
  title,
  infoKey,
  testId,
  children,
}: {
  title: string;
  infoKey?: InfoKey;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <section className={styles.section} aria-label={title} data-testid={testId}>
      <header className={styles.head}>
        <h2 className={styles.title}>{title}</h2>
        {infoKey ? <InfoPopover infoKey={infoKey} /> : null}
      </header>
      <div className={styles.body}>{children}</div>
    </section>
  );
}

/** A row that is a label and a value or control. */
export function SettingsRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{children}</span>
    </div>
  );
}

/** A thing the product does not do yet: its name and "Not available yet". */
export function NotYet({ label, testId }: { label: string; testId?: string }) {
  return (
    <SettingsRow label={label}>
      <span data-testid={testId}>
        <StatusChip tone="neutral" size="sm">
          Not available yet
        </StatusChip>
      </span>
    </SettingsRow>
  );
}
